import React, { useState, useCallback, useEffect } from 'react';
import { Box, Typography } from '@material-ui/core';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash.debounce';

import { InvitationsExport } from 'employee/components/InvitationsExport';
import { DetailBox } from 'common/components/DetailBox';
import { InvitationTable } from 'employee/components/InvitationTable';
import { Button } from 'common/components/buttons/Button';
import { Filters } from 'employee/components/Filters';
import { ActionButton } from 'common/components/buttons/ActionButton';
import { INVITATION_OVERVIEW_VARIABLES } from 'employee/constants';
import { IInvitationsQueryVariables, IInvitationStatus } from 'graphql/types';
import { getIntlPath } from 'common/utils';

import { BasePage } from './BasePage';

const StyledDetailBox = styled(DetailBox)`
    padding: ${({ theme }) => `${theme.spacing(12)}px ${theme.spacing(8)}px`};
    position: relative;
`;

interface IProps {
    className?: string;
}

const BaseInvitationsPage = ({ className }: IProps) => {
    const history = useHistory();
    const { t } = useTranslation();
    const [queryCount, setQueryCount] = useState<number | undefined>();
    const [showFilters, setShowFilters] = useState(false);
    const [queryVariables, setQueryVariables] =
        useState<IInvitationsQueryVariables>(INVITATION_OVERVIEW_VARIABLES);

    useEffect(() => {
        // Reset filters when they are hidden
        if (!showFilters) {
            setQueryVariables({
                ...queryVariables,
                search: '',
                budgetId: '',
            });
        }
    }, [showFilters]);

    const handleSearch = (searchTerm: string) => {
        setQueryVariables({
            ...queryVariables,
            search: searchTerm,
        });
    };

    const handleBudgetFilterChange = (budgetId: string) => {
        setQueryVariables({
            ...queryVariables,
            budgetId,
        });
    };

    const handleStatusFilterChange = (status: IInvitationStatus | '') => {
        setQueryVariables({
            ...queryVariables,
            status: status ? status : undefined,
        });
    };

    const getTitle = () => {
        if (queryCount === undefined) return '';

        return `${queryCount} ${t('employee.invitations')}`;
    };

    const debouncedHandleSearch = useCallback(debounce(handleSearch, 300), []);

    return (
        <BasePage
            Button={
                <Button
                    fullWidth
                    onClick={() => history.push(getIntlPath('newEmployee'))}
                >
                    {t('employee.overview.buttonCreate')}
                </Button>
            }
            className={className}
            pageTitle={t('employee.overview.pageTitle')}
        >
            <Box className="pageDescription">
                <Typography variant="body2">
                    {t('employee.overview.pageDescription')}
                </Typography>
            </Box>

            <Box mt={12}>
                <StyledDetailBox
                    showTitleDivider={false}
                    title={getTitle()}
                    upperRightContent={
                        <Box className="buttonWrapper">
                            <ActionButton
                                showActive={showFilters}
                                onClick={() => setShowFilters(!showFilters)}
                            >
                                {t('employee.searchAndFilter')}
                            </ActionButton>
                            <InvitationsExport />
                        </Box>
                    }
                >
                    {showFilters && (
                        <Filters
                            onBudgetSelect={handleBudgetFilterChange}
                            onCrossIconClick={() => setShowFilters(false)}
                            onSearch={debouncedHandleSearch}
                            onStatusSelect={handleStatusFilterChange}
                        />
                    )}
                    <InvitationTable
                        editable
                        queryVariables={queryVariables}
                        onQueryCountChange={setQueryCount}
                        onQueryVariablesChange={setQueryVariables}
                    />
                </StyledDetailBox>
            </Box>
        </BasePage>
    );
};

export const InvitationsPage = styled(BaseInvitationsPage)`
    .buttonWrapper {
        padding: 10px 0;
    }

    ${InvitationsExport} {
        margin-top: 10px;
    }

    ${Filters} {
        margin-top: 20px;
    }

    @media (min-width: ${({ theme }) =>
            `${theme.breakpoints.values['lg']}px`}) {
        .buttonWrapper {
            display: flex;
            justify-content: flex-end;
        }

        ${InvitationsExport} {
            margin-top: 0;
            margin-left: 10px;
        }
    }
`;
